import { ReactNode, useEffect, useState } from "react";
import clsx from "clsx";
import dayjs from "dayjs";
import Pagination from "@app/components/pagination";
import { DEFAULT_PAGE, DEFAULT_TOTAL_PAGES } from "@app/constants";
import CommissionHistoryService from "@app/services/http/commission.history.service";
import RefCodeService from "@app/services/http/ref.code.service";
import WithdrawService from "@app/services/http/withdraw.service";
import { AccountRefCode, CommissionHistory, Withdraw } from "@app/types";
import { formatDate, formatMoneyVN } from "@app/utils/util";
import useObservable from "@core/hooks/use-observable.hook";
import { useT } from "@app/contexts/preferences.context";
import { IcBanknote, IcReceipt, IcUsers } from "@app/components/icons";
import { LoadingBlock, StatusPill } from "@app/components/ui";

const DAY = 24 * 60 * 60 * 1000;

const th = "whitespace-nowrap bg-surface-2 px-4 py-2.5 text-left text-[11.5px] font-semibold uppercase tracking-[.05em] text-faint";
const td = "border-t border-line px-4 py-3 align-middle";

/** Khung bảng dùng chung: Đang tải… lần đầu, trạng thái rỗng, phân trang (giữ dữ liệu cũ khi đổi trang) */
function ListShell({
  loaded,
  empty,
  emptyIcon,
  emptyTitle,
  emptyText,
  headers,
  page,
  totalPages,
  onPageChange,
  children,
}: {
  loaded: boolean;
  empty: boolean;
  emptyIcon: ReactNode;
  emptyTitle: string;
  emptyText: string;
  headers: string[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  children: ReactNode;
}) {
  const t = useT();

  if (!loaded) return <LoadingBlock />;

  if (empty) {
    return (
      <div className="flex flex-col items-center gap-1.5 px-4 py-12 text-center">
        <div className="mb-2 grid h-14 w-14 place-items-center rounded-2xl bg-brand-soft text-brand">{emptyIcon}</div>
        <h3 className="text-base font-bold">{t(emptyTitle)}</h3>
        <p className="m-0 max-w-[48ch] text-muted">{t(emptyText)}</p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-[13px]">
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={h} className={th}>
                  {t(h)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex justify-end border-t border-line px-5 py-3">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
      )}
    </>
  );
}

const SERVICE: Record<string, string> = { PC: "Thuê máy tính", CLOUD: "Cloud Gaming - Render", VPS: "Thuê VPS" };

/** Lịch sử hoa hồng: CommissionHistoryService.getCommissionHistories như trang cũ */
export function CommissionList({ onCount }: { onCount: (n: number) => void }) {
  const t = useT();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [rows, setRows] = useState<CommissionHistory[]>([]);
  const [loaded, setLoaded] = useState(false);
  const { subscribeUntilDestroy } = useObservable();

  useEffect(() => {
    subscribeUntilDestroy(CommissionHistoryService.getCommissionHistories(page), (res) => {
      setRows(res?.data || []);
      setTotalPages(res?.total_page || DEFAULT_TOTAL_PAGES);
      onCount(res?.count || 0);
      setLoaded(true);
    });
  }, [page]);

  return (
    <ListShell
      loaded={loaded}
      empty={!rows.length}
      emptyIcon={<IcReceipt className="h-[26px] w-[26px]" />}
      emptyTitle="Chưa có hoa hồng nào"
      emptyText="Khi bạn bè dùng mã của bạn để thuê máy, Cloud hoặc VPS, hoa hồng sẽ hiện ở đây."
      headers={["Dịch vụ", "Thao tác", "Mã đơn hàng", "Người dùng", "Hoa hồng", "Thời gian"]}
      page={page}
      totalPages={totalPages}
      onPageChange={setPage}
    >
      {rows.map((row) => (
        <tr key={row._id}>
          <td className={td}>
            <b className="font-semibold">{t(SERVICE[row.service] || row.service)}</b>
          </td>
          <td className={td}>{row.action}</td>
          <td className={clsx(td, "font-mono text-[12.5px] text-muted")}>••••{String(row.service_order_id || "").slice(-6)}</td>
          <td className={clsx(td, "font-mono text-[12.5px]")}>•••{row.account?.username?.slice(-3)}</td>
          <td className={clsx(td, "font-semibold tabular-nums text-ok")}>+{formatMoneyVN(row.commission_money)}đ</td>
          <td className={clsx(td, "whitespace-nowrap tabular-nums text-muted")}>{formatDate(row.created_at)}</td>
        </tr>
      ))}
    </ListShell>
  );
}

/** Người được giới thiệu: RefCodeService.getAccountsByRefCode như trang cũ */
export function ReferredList({ period, onCount }: { period: number; onCount: (n: number) => void }) {
  const t = useT();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [rows, setRows] = useState<AccountRefCode[]>([]);
  const [loaded, setLoaded] = useState(false);
  const { subscribeUntilDestroy } = useObservable();

  useEffect(() => {
    subscribeUntilDestroy(RefCodeService.getAccountsByRefCode(page), (res) => {
      setRows(res?.data || []);
      setTotalPages(res?.total_page || DEFAULT_TOTAL_PAGES);
      onCount(res?.count || 0);
      setLoaded(true);
    });
  }, [page]);

  return (
    <ListShell
      loaded={loaded}
      empty={!rows.length}
      emptyIcon={<IcUsers className="h-[26px] w-[26px]" />}
      emptyTitle="Chưa có ai nhập mã của bạn"
      emptyText="Gửi link giới thiệu cho bạn bè. Người đăng ký bằng link hoặc mã của bạn sẽ hiện ở đây."
      headers={["Người dùng", "Ngày nhập mã", "Hết hạn nhận hoa hồng", "Trạng thái"]}
      page={page}
      totalPages={totalPages}
      onPageChange={setPage}
    >
      {rows.map((row) => {
        // time_ref_code là mili giây (Number trong DB)
        const start = Number(row.time_ref_code);
        const end = start + period * DAY;
        const active = end >= Date.now();

        return (
          <tr key={row.username}>
            <td className={clsx(td, "font-mono text-[12.5px]")}>••••••{row.username.slice(-3)}</td>
            <td className={clsx(td, "whitespace-nowrap tabular-nums")}>{formatDate(start)}</td>
            <td className={clsx(td, "whitespace-nowrap tabular-nums")}>
              {formatDate(end)}
              {active && (
                <small className="ml-1.5 text-xs text-muted">
                  ({t("còn")} {Math.max(0, dayjs(end).diff(dayjs(), "day"))} {t("ngày")})
                </small>
              )}
            </td>
            <td className={td}>
              <StatusPill tone={active ? "ok" : "muted"}>{active ? t("Còn hiệu lực") : t("Hết hạn")}</StatusPill>
            </td>
          </tr>
        );
      })}
    </ListShell>
  );
}

/** Yêu cầu rút tiền: WithdrawService.getRequestWithdraw như trang cũ */
export function WithdrawList({ reload, onCount }: { reload: number; onCount: (n: number) => void }) {
  const t = useT();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [totalPages, setTotalPages] = useState(DEFAULT_TOTAL_PAGES);
  const [rows, setRows] = useState<Withdraw[]>([]);
  const [loaded, setLoaded] = useState(false);
  const { subscribeUntilDestroy } = useObservable();

  useEffect(() => {
    subscribeUntilDestroy(WithdrawService.getRequestWithdraw(page), (res) => {
      setRows(res?.data || []);
      setTotalPages(res?.total_page || DEFAULT_TOTAL_PAGES);
      onCount(res?.count || 0);
      setLoaded(true);
    });
  }, [page, reload]);

  const status = (value?: number): [string, "ok" | "warn" | "bad"] =>
    value === 0 ? ["Đang xử lý", "warn"] : value === 1 ? ["Thành công", "ok"] : ["Đã huỷ", "bad"];

  return (
    <ListShell
      loaded={loaded}
      empty={!rows.length}
      emptyIcon={<IcBanknote className="h-[26px] w-[26px]" />}
      emptyTitle="Chưa có yêu cầu rút tiền"
      emptyText="Bấm Rút tiền ở ví hoa hồng khi số dư đạt tối thiểu 500.000đ."
      headers={["Mã yêu cầu", "Số tiền", "Ngân hàng", "Số tài khoản", "Chủ tài khoản", "Trạng thái"]}
      page={page}
      totalPages={totalPages}
      onPageChange={setPage}
    >
      {rows.map((row) => {
        const [label, tone] = status(row.is_succeeded);

        return (
          <tr key={row._id}>
            <td className={clsx(td, "font-mono text-[12.5px] text-muted")}>{String(row._id || "").slice(-8).toUpperCase()}</td>
            <td className={clsx(td, "font-semibold tabular-nums")}>{formatMoneyVN(row.money)}đ</td>
            <td className={td}>{row.bank_name}</td>
            <td className={clsx(td, "font-mono text-[12.5px]")}>{row.bank_number}</td>
            <td className={td}>{row.bank_owner}</td>
            <td className={td}>
              <StatusPill tone={tone}>{t(label)}</StatusPill>
            </td>
          </tr>
        );
      })}
    </ListShell>
  );
}
